import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import './Notification.css';
import { MdCheckCircle, MdError, MdInfo, MdClose } from 'react-icons/md';

const NotificationContext = createContext();

export const useNotification = () => {
    const context = useContext(NotificationContext);
    if (!context) {
        throw new Error('useNotification must be used within a NotificationProvider');
    }
    return context;
};

export const NotificationProvider = ({ children }) => {
    const [notification, setNotification] = useState(null);
    
    // Helper to get company-scoped storage key
    const getStorageKey = useCallback(() => {
        try {
            const user = JSON.parse(localStorage.getItem('user'));
            const companyId = user?.company_id || 'default';
            return `notification_sound_${companyId}`;
        } catch (e) {
            return 'notification_sound_default';
        }
    }, []);

    const [isSoundEnabled, setIsSoundEnabled] = useState(() => {
        const key = getStorageKey();
        const saved = localStorage.getItem(key);
        return saved !== null ? JSON.parse(saved) : true;
    });

    // Handle switching between accounts/companies
    useEffect(() => {
        const key = getStorageKey();
        const saved = localStorage.getItem(key);
        setIsSoundEnabled(saved !== null ? JSON.parse(saved) : true);
    }, [getStorageKey]);

    // Create audio object once
    const [notificationSound] = useState(new Audio('https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3'));

    useEffect(() => {
        const key = getStorageKey();
        localStorage.setItem(key, JSON.stringify(isSoundEnabled));
    }, [isSoundEnabled, getStorageKey]);

    const showNotification = useCallback((message, type = 'info', duration = 3000) => {
        if (isSoundEnabled) {
            notificationSound.currentTime = 0;
            notificationSound.play().catch(err => console.log("Audio play blocked by browser policy"));
        }
        setNotification({ message, type });
        setTimeout(() => {
            setNotification(null);
        }, duration);
    }, [isSoundEnabled, notificationSound]);

    const closeNotification = () => setNotification(null);

    return (
        <NotificationContext.Provider value={{ showNotification, isSoundEnabled, setIsSoundEnabled }}>
            {children}
            {notification && (
                <div className={`notification_toast ${notification.type}`}>
                    <div className="notification_icon">
                        {notification.type === 'success' && <MdCheckCircle />}
                        {notification.type === 'error' && <MdError />}
                        {notification.type === 'info' && <MdInfo />}
                    </div>
                    <div className="notification_message">{notification.message}</div>
                    <button className="notification_close" onClick={closeNotification}>
                        <MdClose />
                    </button>
                </div>
            )}
        </NotificationContext.Provider>
    );
};
